import imageUrlBuilder from "@sanity/image-url"

const builder = imageUrlBuilder({
  projectId: process.env.GATSBY_SANITY_PROJECT,
  dataset: process.env.GATSBY_SANITY_DATASET,
})

function get(image) {
  return builder.image(image)
}

function getFluidFromAsset(image, maxWidth) {
  const ar = image.asset.metadata.dimensions.aspectRatio
  const fileWidth = image.asset.metadata.dimensions.width
  const widths = [
    Math.round(maxWidth / 4),
    Math.round(maxWidth / 2),
    maxWidth,
    ...(maxWidth * 1.5 <= fileWidth ? [maxWidth * 1.5] : []),
    ...(maxWidth * 2 <= fileWidth ? [maxWidth * 2] : []),
    ...(maxWidth * 3 <= fileWidth ? [maxWidth * 3] : []),
    ...(maxWidth * 4 <= fileWidth ? [maxWidth * 4] : []),
  ]
  return {
    aspectRatio: image.asset.metadata.dimensions.aspectRatio,
    src: get(image)
      .width(maxWidth)
      .height(Math.ceil(maxWidth / ar))
      .fit("crop")
      .url(),
    srcWebp: get(image)
      .width(maxWidth)
      .height(Math.ceil(maxWidth / ar))
      .fit("crop")
      .format("webp")
      .url(),
    srcSet: widths
      .map(
        widthEntry =>
          `${get(image)
            .width(widthEntry)
            .height(Math.ceil(widthEntry / ar))
            .fit("crop")
            .url()} ${widthEntry}w`
      )
      .join(","),
    srcSetWebp: widths
      .map(
        widthEntry =>
          `${get(image)
            .width(widthEntry)
            .height(Math.ceil(widthEntry / ar))
            .fit("crop")
            .format("webp")
            .url()} ${widthEntry}w`
      )
      .join(","),
    sizes: `(max-width: ${maxWidth}px) 100vw, ${maxWidth}px`,
    base64: image.asset.metadata.lqip,
  }
}

function getFluidHeightFromAsset(image, maxHeight) {
  const ar = image.asset.metadata.dimensions.aspectRatio
  const fileHeight = image.asset.metadata.dimensions.height
  const heights = [
    Math.round(maxHeight / 4),
    Math.round(maxHeight / 2),
    maxHeight,
    ...(maxHeight * 1.5 <= fileHeight ? [maxHeight * 1.5] : []),
    ...(maxHeight * 2 <= fileHeight ? [maxHeight * 2] : []),
    ...(maxHeight * 3 <= fileHeight ? [maxHeight * 3] : []),
    ...(maxHeight * 4 <= fileHeight ? [maxHeight * 4] : []),
  ]
  return {
    aspectRatio: image.asset.metadata.dimensions.aspectRatio,
    src: get(image)
      .width(Math.round(maxHeight * ar))
      .height(maxHeight)
      .fit("crop")
      .url(),
    srcWebp: get(image)
      .width(Math.round(maxHeight * ar))
      .height(maxHeight)
      .fit("crop")
      .format("webp")
      .url(),
    srcSet: heights
      .map(
        heightEntry =>
          `${get(image)
            .width(Math.round(heightEntry * ar))
            .height(heightEntry)
            .fit("crop")
            .url()} ${Math.round(heightEntry * ar)}w`
      )
      .join(","),
    srcSetWebp: heights
      .map(
        heightEntry =>
          `${get(image)
            .width(Math.round(heightEntry * ar))
            .height(heightEntry)
            .fit("crop")
            .format("webp")
            .url()} ${Math.round(heightEntry * ar)}w`
      )
      .join(","),
    sizes: `(max-width: ${maxHeight * ar}px) 100vw, ${maxHeight + ar}px`,
    base64: image.asset.metadata.lqip,
  }
}

function getFixedFromAsset(image, { width: widthProvided, height: heightProvided }) {
  const width = widthProvided || heightProvided * image.asset.metadata.dimensions.aspectRatio
  const height = heightProvided || widthProvided / image.asset.metadata.dimensions.aspectRatio
  const fileWidth = image.asset.metadata.dimensions.width
  const fileHeight = image.asset.metadata.dimensions.height

  const sizes = [
    { name: "1x", width, height },
    ...(width * 1.5 >= fileWidth && height * 1.5 >= fileHeight
      ? [{ name: "1.5x", width: width * 1.5, height: height * 1.5 }]
      : []),
    ...(width * 2 >= fileWidth && height * 2 >= fileHeight
      ? [{ name: "2x", width: width * 2, height: height * 2 }]
      : []),
    ...(width * 3 >= fileWidth && height * 3 >= fileHeight
      ? [{ name: "3x", width: width * 3, height: height * 3 }]
      : []),
  ]
  return {
    width: width,
    height: height,
    src: get(image)
      .width(width)
      .height(height)
      .fit("crop")
      .url(),
    srcWebp: get(image)
      .width(width)
      .height(height)
      .fit("crop")
      .format("webp")
      .url(),
    srcSet: sizes
      .map(
        size =>
          `${get(image)
            .width(size.width)
            .height(size.height)
            .fit("crop")
            .url()} ${size.name}`
      )
      .join(","),
    srcSetWebp: sizes
      .map(
        size =>
          `${get(image)
            .width(size.width)
            .height(size.height)
            .fit("crop")
            .format("webp")
            .url()} ${size.name}`
      )
      .join(","),
    base64: image.asset.metadata.lqip,
  }
}

export { getFluidFromAsset, getFluidHeightFromAsset, getFixedFromAsset }
